/**
 * Placing and measuring a memory constellation (ZhKnowledgeConstellation): every memory a dot in one field, the
 * links between them a faint web. Nothing here touches the DOM, so the field a page draws is the field a spec
 * measures.
 *
 * A dot's place comes from its id alone. The same memory sits at the same angle whatever else is on the page, so
 * narrowing a filter thins the field instead of reshuffling it.
 */
import type { ConstellationLinkView, ConstellationUnitView } from './surfaces'

export interface PlacedUnit {
    unit: ConstellationUnitView
    /** Where it sits, in world units around the field's centre. */
    x: number
    y: number
    /** Its own offset into the ambient motion, 0 to 2π, so the field breathes out of step. */
    phase: number
}

export interface IndexedLink {
    link: ConstellationLinkView
    /** Its two ends, as positions in the units it was indexed against. */
    a: number
    b: number
}

export interface LinkIndex {
    links: IndexedLink[]
    /** For each unit, the positions in `links` of the links that touch it. */
    byUnit: number[][]
}

/** How a field is looked at: world units to screen pixels, then moved. */
export interface ConstellationView {
    zoom: number
    panX: number
    panY: number
}

/** World units of room each memory gets: the field's reach grows with the square root of how many there are. */
const SPREAD = 30
/** The field fills this share of the canvas's shorter side when fitted. */
const FIT_SHARE = 0.8
/** A handful of memories is not blown up to fill the canvas: past this they would be dots a hand apart. */
const MAX_FIT_ZOOM = 2
/** How far a dot wanders from its place, in screen pixels at full size. */
const DRIFT = 16
const TURN = 2 * Math.PI

/**
 * An id as a fraction between 0 and 1: the same id always lands in the same place. FNV-1a, then a final mix, so ids
 * that differ by one character (`note-1`, `note-2`) land far apart instead of side by side.
 */
function fraction(text: string): number {
    let hash = 0x811c9dc5
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i)
        hash = Math.imul(hash, 0x01000193)
    }
    hash ^= hash >>> 16
    hash = Math.imul(hash, 0x85ebca6b)
    hash ^= hash >>> 13
    hash = Math.imul(hash, 0xc2b2ae35)
    hash ^= hash >>> 16
    return (hash >>> 0) / 2 ** 32
}

/** Every memory at its own place in one disc, evenly dense from the centre out. */
export function placeConstellation(units: readonly ConstellationUnitView[]): PlacedUnit[] {
    const reach = Math.sqrt(Math.max(units.length, 1)) * SPREAD
    return units.map(unit => {
        const angle = fraction(unit.id) * TURN
        const radius = reach * Math.sqrt(fraction(`${unit.id}/radius`))
        return {
            unit,
            x: Math.cos(angle) * radius,
            y: Math.sin(angle) * radius,
            phase: fraction(`${unit.id}/phase`) * TURN,
        }
    })
}

/** The links whose two ends are both on the page, and for each memory the links that touch it. */
export function indexLinks(
    units: readonly ConstellationUnitView[],
    links: readonly ConstellationLinkView[],
): LinkIndex {
    const position = new Map(units.map((unit, i) => [unit.id, i]))
    const byUnit: number[][] = units.map(() => [])
    const indexed: IndexedLink[] = []
    for (const link of links) {
        const a = position.get(link.source)
        const b = position.get(link.target)
        if (a === undefined || b === undefined || a === b) continue
        byUnit[a]?.push(indexed.length)
        byUnit[b]?.push(indexed.length)
        indexed.push({ link, a, b })
    }
    return { links: indexed, byUnit }
}

/** The first and last moment any memory on the page was mentioned, or null when none says. */
export function mentionedRange(units: readonly ConstellationUnitView[]): { min: number; max: number } | null {
    const moments = units.flatMap(unit => (unit.at === null ? [] : [unit.at]))
    if (moments.length === 0) return null
    return { min: Math.min(...moments), max: Math.max(...moments) }
}

/** How recent a memory is among those on the page, 0 (the oldest) to 1 (the newest). Undated reads as oldest. */
export function heatOf(at: number | null, range: { min: number; max: number } | null): number {
    if (at === null || range === null) return 0
    if (range.max === range.min) return 1
    return Math.min(1, Math.max(0, (at - range.min) / (range.max - range.min)))
}

/**
 * A dot's radius in pixels at full size. A memory consolidated from others is sized by how many source facts stand
 * behind it; any other by how many links it has, in a narrow range, like star magnitudes.
 */
export function radiusOf(sourceFacts: number | null | undefined, linkCount: number): number {
    if (sourceFacts && sourceFacts > 0) return 3 + Math.min(Math.sqrt(sourceFacts - 1) * 2, 11)
    return 2.5 + Math.min(linkCount * 0.15, 2.5)
}

/** The zoom at which the whole field sits inside a canvas of this size. */
export function fitZoom(placed: readonly PlacedUnit[], width: number, height: number): number {
    const reach = placed.reduce((far, { x, y }) => Math.max(far, Math.hypot(x, y)), 0)
    if (reach === 0 || width <= 0 || height <= 0) return 1
    return Math.min(MAX_FIT_ZOOM, (Math.min(width, height) * FIT_SHARE) / (reach * 2))
}

/**
 * Where a placed memory is on a canvas of this size, `seconds` into the ambient motion. The drift is bounded in
 * pixels, so zooming in spreads the memories apart without making them swing wider.
 */
export function toScreen(
    placed: PlacedUnit,
    view: ConstellationView,
    width: number,
    height: number,
    seconds: number,
): { x: number; y: number } {
    const swing = DRIFT * Math.min(view.zoom, 1)
    return {
        x: width / 2 + view.panX + placed.x * view.zoom + swing * Math.sin(seconds * 0.6 + placed.phase),
        y: height / 2 + view.panY + placed.y * view.zoom + swing * Math.cos(seconds * 0.5 + placed.phase * 1.3),
    }
}

/** The dot nearest a point, within `reach` pixels of it, as a position in `xs`/`ys`; -1 when none is. */
export function nearest(xs: ArrayLike<number>, ys: ArrayLike<number>, x: number, y: number, reach: number): number {
    let best = -1
    let bestDistance = reach
    for (let i = 0; i < xs.length; i += 1) {
        const distance = Math.hypot(x - (xs[i] ?? 0), y - (ys[i] ?? 0))
        if (distance < bestDistance) {
            bestDistance = distance
            best = i
        }
    }
    return best
}

/** The pan that keeps the world point under a pointer where it is while the zoom changes. */
export function zoomAbout(
    view: ConstellationView,
    zoom: number,
    pointerX: number,
    pointerY: number,
    width: number,
    height: number,
): ConstellationView {
    const [fromCentreX, fromCentreY] = [pointerX - width / 2, pointerY - height / 2]
    const scale = zoom / view.zoom
    return {
        zoom,
        panX: fromCentreX - (fromCentreX - view.panX) * scale,
        panY: fromCentreY - (fromCentreY - view.panY) * scale,
    }
}
